import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import Dropdown from "../commons/Dropdown"
import { getAllCategories, getCategoryById } from "../../firebase/categories"
import { bulkUpdateShopifyProductCategory } from "../../firebase/shopify"

/**
 * One category applied to every selected listing.
 *
 * Deliberately starts blank rather than pre-filled from the selection: the
 * listings being set together may hold different categories, and showing one
 * of them would suggest the rest already agree.
 *
 * `onSaved` is handed the assignment so the caller can patch its rows in
 * place. Refetching the whole table instead would drop the seller back to
 * the top of a long list after every save, which is the thing bulk editing
 * is meant to stop.
 */
export default function DialogBulkShopifyCategory({ open, items = [], onClose, onSaved }) {
    const [categories, setCategories] = useState([])
    const [subCategories, setSubCategories] = useState([])
    const [category, setCategory] = useState("")
    const [subCategory, setSubCategory] = useState("")
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return

        getAllCategories().then((cats) => {
            setCategories(cats.map((cat) => ({ value: cat.id, label: cat.name.toUpperCase() })))
        })

        setCategory("")
        setSubCategory("")
    }, [open])

    useEffect(() => {
        if (!category) {
            setSubCategories([])
            setSubCategory("")
            return
        }

        getCategoryById(category).then((data) => {
            setSubCategories((data?.subCategories || []).map((cat) => ({ value: cat, label: cat.toUpperCase() })))
            // The previous category's sub-category cannot apply to this one.
            setSubCategory("")
        })
    }, [category])

    const handleSave = async () => {
        if (!category) {
            toast.error('Select a category first.')
            return
        }

        setSaving(true)
        try {
            const result = await bulkUpdateShopifyProductCategory(
                items.map((item) => item.id),
                category,
                subCategory
            )
            toast.success(`${result.updated} listing${result.updated === 1 ? '' : 's'} updated.`)
            onSaved?.({ category, subCategory })
        } catch (error) {
            console.error('Failed to bulk update Shopify listing categories:', error)
            toast.error(error.message || 'Failed to update categories')
        } finally {
            setSaving(false)
        }
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[10000] w-screen overflow-y-auto">
            {/* No backdrop-blur here: it's a known Chromium GPU-compositing
                conflict with native <select> dropdowns -- with the blur on,
                clicking an option can silently fail to register while every
                other click on the dialog still works fine. */}
            <div className="fixed inset-0 bg-[#000] bg-opacity-75" onClick={onClose} />

            <div className="relative flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-[480px] border-[1px] border-gray bg-[#141414] p-6">
                    <button onClick={onClose} className="absolute right-4 top-4">
                        <img src="/assets/icons/icon-close-white.svg" alt="close" />
                    </button>

                    <h2 className="text-white text-xl mb-1">Set Category</h2>
                    <p className="text-sm text-[#717171] mb-6">
                        Applies to {items.length} selected listing{items.length === 1 ? '' : 's'}.
                    </p>

                    <div className="flex flex-col gap-5">
                        <label className="w-full text-white">
                            <span className="block mb-2 text-sm">Category</span>
                            <div className="border-[1px] border-gray">
                                <Dropdown
                                    options={categories}
                                    value={category}
                                    placeholder="Select Category"
                                    onChange={(e) => setCategory(e.target.value)}
                                />
                            </div>
                        </label>

                        <label className="w-full text-white">
                            <span className="block mb-2 text-sm">Sub-Category</span>
                            <div className="border-[1px] border-gray">
                                <Dropdown
                                    options={subCategories}
                                    value={subCategory}
                                    placeholder="Select Sub-Category"
                                    onChange={(e) => setSubCategory(e.target.value)}
                                />
                            </div>
                        </label>

                        {/* Leaving it blank is allowed, so say what that
                            means: the listing shows under the category with
                            no sub-category of its own. */}
                        <p className="text-xs text-[#717171]">
                            Optional. Left blank, these listings appear under the category itself.
                        </p>
                    </div>

                    <button
                        className="solo-button w-full mt-8 text-white border-white border-[1px] bg-primary h-[45px] disabled:opacity-60"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : `Apply to ${items.length} listing${items.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        </div>
    )
}
